import {Inject, Optional, Pipe, PipeTransform} from '@angular/core';
import {TISA_CLOUDFRONT_ID} from '@tisa-group/core/providers';
import {CLOUDFRONT_ID_REQUIRED} from './image-variant.errors';
import {UmpireImage} from '@tisa-group/models';
import {CheckWebpSupportService} from '@tisa-group/core/services';

export interface ImageVariantSize {
  height: number;
  width: number;
}

export interface ImageVariantFilters {
  background_color?: string;
  blur?: number;
  fill?: string;
  convolution?: string;
  equalize?: boolean;
  autojpg?: boolean;
  grayscale?: boolean;
  format?: string;
  no_upscale?: boolean;
  proportion?: string;
  quality?: number;
  rgb?: string;
  rotate?: number;
  sharpen?: string;
  stretch?: boolean;
  strip_exif?: boolean;
  strip_icc?: boolean;
  upscale?: boolean;
  watermark?: string;
}

export type ImageVariantParameter = ImageVariantSize & ImageVariantFilters;

@Pipe({
  name: 'imageVariant',
  standalone: false,
})
export class ImageVariantPipe implements PipeTransform {
  constructor(
    @Optional() @Inject(TISA_CLOUDFRONT_ID) readonly tisaCloudFrontId: string | undefined,
    @Optional()
    @Inject(CheckWebpSupportService)
    readonly checkWebpSupportService: CheckWebpSupportService | null,
  ) {
    if (!tisaCloudFrontId) {
      throw CLOUDFRONT_ID_REQUIRED;
    }
  }

  transform(
    value: Partial<UmpireImage> | undefined,
    params?: ImageVariantParameter,
  ): string {
    if (!value) {
      return '';
    }
    const {path} = value;
    let url = '';

    if (params && params.width >= 0 && params.height >= 0) {
      url += `fit-in/${params.width}x${params.height}`;
    }

    const {width, height, ...config} = params || {};

    if (Object.keys(config).length > 0) {
      let isWebpSupported = false;
      if (this.checkWebpSupportService) {
        isWebpSupported = this.checkWebpSupportService?.isWebpSupported();
      }
      url += ImageVariantPipe.getFiltersString(config, isWebpSupported);
    }

    return encodeURI(`https://${this.tisaCloudFrontId}.cloudfront.net/${url}/${path}`);
  }

  static getFiltersString(config: ImageVariantFilters, isWebpSupported: boolean): string {
    return Object.keys(config).reduce((acc, curr: string) => {
      let string = 'filters:';
      const key = curr as keyof ImageVariantFilters;

      // Handle format conversion based on WebP support
      if (curr === 'format' && config[curr] === '.webp') {
        if (!isWebpSupported) {
          config[curr] = '.jpeg';
        }
      }

      if (typeof config[key] === 'boolean' && config[key] === true) {
        string += `${curr}()`;
      } else {
        string += `${curr}(${config[key]})`;
      }

      return `${acc}/${string}`;
    }, '');
  }
}
